import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { IDragFileBrowseData, IReturnChangesWidgetEditForm, IWidgetReturnTranslatedValues, checkIfFieldDisabled, deepCopy, languagesList_, layoutTypes, makeid, prepareToSaveImagesWidgets, widgetEditMode } from 'na-lib';
import { CommentsService } from 'src/app/service/comments.service';
import { SettingsService } from 'src/app/service/settings.service';
import { UploadService } from 'src/app/service/upload.service';
import { supportUrlStructure } from 'src/app/supportUrls';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { MediaDialogHandlerComponent } from 'src/app/media/media-dialog-handler/media-dialog-handler.component';
import { MediaDetailsDialogComponent } from '../media-details-dialog/media-details-dialog.component';
import { ImageCompressionService } from 'src/app/service/image-compression.service';
import { FileHandle } from 'src/app/directive/drag-and-drop.directive';
import { Subject, takeUntil } from 'rxjs';
import { displayArrayKeyPath, getArrayKeyPaths } from '../../array-key-paths';

interface AnchorOption {
  id: string;
  label?: string;
  name?: string;
}
interface ChildPageOption {
  id: string;
  name?: string;
}

@Component({
    selector: 'app-images-edit-form',
    templateUrl: './images-edit-form.component.html',
    styleUrl: './images-edit-form.component.css',
    standalone: false
})

export class ImagesEditFormComponent {
  @Input() data:widgetEditMode
  @Output() returnChanges = new EventEmitter<IReturnChangesWidgetEditForm>();
  @ViewChild('fileInput') fileInput: ElementRef<HTMLInputElement>;
  @Output() formChange = new EventEmitter<FormGroup>();

  imagesEditData:any
  oldData:any
  defaultLanguage:string
  widgetNumber:any
  passId:any
  url:any = {}
  translationModeOriginal = false
  dragFileData: IDragFileBrowseData
  imagesEditForm!:FormGroup
  allImages:any
  layoutSelected:any
  layoutImages:any
  layoutBad=false
  userHasBetaWidgetRole = false
  imagesSizes:any = {}
  selectedtab:any
  imagesData:any=[]
  imagesRef: any[] = [];
  allImagesAfterDelete:any=[]
  imgFromImgNew:any=[]
  newImage:any=[]
  newImagesURLs:any = []
  imagesToDelete:any = []
  files: FileHandle[] = []
  drop=false
  trackByLayout = (_: number, item: any) => item.value;
  layoutTypes = layoutTypes
  originalImagesData:any = []
  contextLicenseFree:any = false

  anchorOptions:any
  childPages:any
  arrayKeyPaths: string[] = [];
  displayArrayKeyPath = displayArrayKeyPath;

  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    public dialog: MatDialog,
    private uploadService: UploadService,
    private translate:TranslateService,
    private settingsService: SettingsService,
    private cdr: ChangeDetectorRef,
    private commentService: CommentsService,
    private imageCompressionService: ImageCompressionService) { }

  ngOnInit():void{
    this.arrayKeyPaths = getArrayKeyPaths((this.data as any)?.contentTypeSchema);
    if(this.settingsService.checkIfBetaRoleAccess())
      this.userHasBetaWidgetRole = true

    this.defaultLanguage = this.data.defaultLanguage
    this.imagesEditData = deepCopy(this.data.values)
    this.oldData = deepCopy(this.imagesEditData)
    this.passId = this.data.passId
    this.widgetNumber = this.data.widget

    if(this.data.translateMode){
      let translateData = deepCopy(this.data.translateData)
      const {fields, originalLanguageData, defaultLanguageWidgetData, valuesTranslated, selectedLanguage, values} = translateData

      if(translateData.originalLanguageData){
        this.translationModeOriginal = true
        this.dragFileData = translateData.dragFileDataOriginal
        this.initTranslateEditData( defaultLanguageWidgetData, valuesTranslated, originalLanguageData, [],selectedLanguage)
      }else{
        this.translationModeOriginal = false

        this.dragFileData = translateData.dragFileData
        this.originalImagesData = deepCopy(values.images)
        this.initTranslateEditData(values,valuesTranslated, originalLanguageData, fields, selectedLanguage)
      }
    }
    else{
      this.initWidgetEdit()
      const extra = this.data as widgetEditMode & {
        anchorOptions?: AnchorOption[];
        childPages?: ChildPageOption[];
      };

      this.anchorOptions = extra.anchorOptions || [];
      this.childPages   = extra.childPages   || [];

      this.dragFileData = {
        calledFrom: ImagesEditFormComponent,
        calledFromData:{
          passId: this.passId,
          widgetNumber:this.widgetNumber,
          widget: "images",
          imageIndex: this.imagesEditForm.value.images?.length
        },
        enableDragDrop: true,
        enableBrowse:true,
        enableMedia:true,
        enableEnterUrl:true,
        enableMultipleDragDrop:true,
        supportedFileTypes:['image/jpeg', 'image/png', 'image/webp'],
        supportedFileAccept:".jpg, .jpeg, .png, .webp",
        maxSizeInBytes:2 * 1024 * 1024, // 2 MB size limit for compression
        maxAllowedSizeInBytes:20 * 1024 * 1024, // 20 MB size limit for direct upload
        returnSupportUrl: undefined,
        returnUrlData: undefined,
        enterUrl:false,
        returnUrlType: undefined
      }
    }

    this.contextLicenseFree = this.settingsService.contextLicenseFreePublisher$.value
  }

  selectArrayKeyPath(path: string): void {
    const control = this.imagesEditForm.get('keyPath');
    control?.setValue(path);
    control?.markAsDirty();
    control?.markAsTouched();
  }


  initTranslateEditData(data, valuesTranslated, originalLanguageData, fields, selectedLanguage){

    let {id, layout, type, title, subtitle, images, mediaURL, name, keyPath, filter, widgetStyle} = data

    if(!originalLanguageData){
      title = valuesTranslated[selectedLanguage +'.'+id+'.title']
      subtitle = valuesTranslated[selectedLanguage +'.'+id+'.subtitle']

      images.forEach(image => {
        image.title = valuesTranslated[selectedLanguage + '.' + id + '_' + image.id + '.title']
        image.url = valuesTranslated[selectedLanguage + '.' + id + '_' + image.id + '.url']
        image.linkURL = valuesTranslated[selectedLanguage + '.' + id + '_' + image.id + '.linkURL']
        image.altTitle = valuesTranslated[selectedLanguage + '.' + id + '_' + image.id + '.altTitle']
      });
    }

    images?.forEach( (image, index) => {
      if(image.url)
        this.url[image.id] = supportUrlStructure( image.url, this.passId, this.settingsService.contextId$)
      else
        this.url[image.id] = supportUrlStructure( this.originalImagesData[index].url, this.passId, this.settingsService.contextId$)

    })

    this.imagesEditForm =  this.fb.group({
      id:[{value: id? id : undefined, disabled: checkIfFieldDisabled('id', fields, originalLanguageData)}],
      layout: [{value:layout ? layout : undefined , disabled: checkIfFieldDisabled('layout', fields, originalLanguageData)}],//
      type: [{value:type ? type: undefined,disabled: false}],//checkIfFieldDisabled('type', fields, originalLanguageData)
      title: [{value:title ? title : undefined , disabled: checkIfFieldDisabled('title', fields, originalLanguageData)}],
      subtitle: [{value:subtitle ? subtitle : undefined , disabled: checkIfFieldDisabled('subtitle', fields, originalLanguageData)}],
      images: [{value:images ? images : undefined , disabled: false}],//checkIfFieldDisabled('images', fields, originalLanguageData)
      mediaURL: [{value:mediaURL ? mediaURL : undefined , disabled: checkIfFieldDisabled('mediaURL', fields, originalLanguageData)}],
      name: [{value:name ? name : undefined , disabled: checkIfFieldDisabled('name', fields, originalLanguageData)}],
      keyPath: [{value:keyPath ?? '', disabled: true}],
      widgetStyle: [{value:widgetStyle ? widgetStyle : undefined , disabled: checkIfFieldDisabled('widgetStyle', fields, originalLanguageData)}],
      filter: [{value:filter ? filter : undefined , disabled: checkIfFieldDisabled('filter', fields, originalLanguageData)}],
    })

    this.imagesEditForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value:any)=> {
      if(!originalLanguageData){
        this.returnChanges.emit({
          type:"images",
          form: this.imagesEditForm,
          validForm: this.imagesEditForm.valid,
          translations:{subtitle:value.subtitle, title: value.title, images: value.images},
          saveDisabled: false
        })
      }
    })
  }

  initWidgetEdit(){
    let {id, layout, type, title, subtitle, images, mediaURL, name, keyPath, filter, widgetStyle} = this.imagesEditData

    if(images){
      images.forEach((image, index) => {
        if(image.url)
          this.supportUrl(image.url, index)
      });
    }

    this.allImages = deepCopy(images)
    this.imagesData = images

    if(layout)
      this.layoutSelected = layout
    else
      this.layoutSelected = "horizontal"

    this.widgetNumber= this.data['widget']
    this.layoutTypes.forEach((lay:any)=>{
      if(lay.value==this.layoutSelected)
        this.layoutImages = lay.images
    })

    if(this.layoutSelected == "imagesHorizontal")
      this.layoutImages="2"

    if(type == "imagesHorizontal"){
      layout = "horizontal"
      type = "images"
    }

    if(type == "imageBlocks")
      layout = "images"

    this.imagesEditForm = this.fb.group({
      id:[{value: id ? id : undefined, disabled:false}],
      layout:[{value: layout ? layout : undefined, disabled:false}],
      type:[{value: type ? type :undefined, disabled:false}],
      title:[{value: title ? title : undefined, disabled:false}],
      subtitle:[{value: subtitle ? subtitle :undefined, disabled:false}],
      images:[{value: images ? images : undefined, disabled:false}],
      mediaURL:[{value: mediaURL ? mediaURL : undefined, disabled:false}],
      name:[{value: name ? name : undefined, disabled:false}],
      keyPath:[{value: keyPath ?? '', disabled:false}],
      filter:[{value:  filter ? filter : undefined, disabled:false}],
      widgetStyle:[{value:  widgetStyle ? widgetStyle : undefined, disabled:false}]
    })

    this.checkMissingImgSize(images)

    if(!this.imagesEditForm.value.keyPath?.trim() && this.layoutImages>this.imagesEditForm.value?.images?.length)
      this.layoutBad=true
    else
      this.layoutBad=false

    this.imagesEditForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe( value => {
      this.layoutSelected = value.layout

      this.layoutImages = this.layoutTypes.find(layout => layout.value == this.layoutSelected)?.images
      if(this.layoutSelected=="imagesHorizontal")
        this.layoutImages="2"

      if(!value.keyPath?.trim() && this.layoutImages>this.imagesEditForm.value?.images?.length)
        this.layoutBad=true
      else
        this.layoutBad=false

      if(value.images){
        const allImages = value.images
        this.dragFileData.calledFromData.imageIndex = allImages?.length
        this.checkMissingImgSize(allImages)
      }

      this.returnChanges.emit({
          type:"images",
          form: this.imagesEditForm,
          validForm: this.imagesEditForm.valid,
          data:value,
          saveDisabled: this.layoutBad
        }
      )
    })


    this.imagesEditForm.get('images')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe( images => {
      this.checkMissingIds(images)
    })

    this.updateImgsRef()
  }

  updateImgsRef(){
    const updated = this.imagesEditForm.get('images')?.value ?? [];
    this.imagesRef.length = 0;
    this.imagesRef = deepCopy(updated);
    // this.imagesRef.push(...updated);
    this.cdr.markForCheck();
  }

  checkMissingImgSize(images){
    images?.forEach(async image => {
      if ((image.size?.length == 0 || (image.size && image.size[0] == 0 && image.size[1] == 0) || !image.size)
        && !/^\$\{[^}]+\}$/.test(image.url ?? '')) {
        const url = supportUrlStructure(image.url, this.passId, this.settingsService.contextId$)

        await this.uploadService.calculateImgFileSize(url).then(value => {
          this.imagesSizes[image.url] = value
          image.size = value
        })
      }
    })
  }

  checkMissingIds(images){
    let addedId = false
    const missingId = images.find( image => !image.id)
    if(missingId){
      images.forEach(image => {
        if(!image.id){
          image.id = makeid(5)
          addedId = true
        }
      });
      if(addedId)
        this.imagesEditForm.patchValue({images: images})
    }
  }

  supportUrl(url, index){
    if(url ){
      const firebaseUrl = supportUrlStructure(url, this.passId, this.settingsService.contextId$)
      this.url[url]=firebaseUrl
      // this.url[index]=firebaseUrl
    }
  }

  async readURL(event: any): Promise<void> {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      const maxSizeInBytes = 2 * 1024 * 1024; // 2 MB size limit for compression
      const maxAllowedSizeInBytes = 20 * 1024 * 1024; // 20 MB size limit for direct upload

      // Check for supported file types
      const supportedTypes = ['image/jpeg', 'image/png', 'image/webp'];
      if (!supportedTypes.includes(file.type)) {
        // Unsupported file type
        const message = this.translate.instant("SnackBarConstants.UNSUPPORTED_IMG_TYPE");
        this.commentService.addSnackBar.emit(message);
        return;
      }

      if (file.size > maxAllowedSizeInBytes) {
        // File is too large for both upload and compression
        const message = this.translate.instant("SnackBarConstants.IMAGE_FILE_TOO_LARGE");
        this.commentService.addSnackBar.emit(message);
        return;
      }

      let imageToUpload = file;

      if (file.size > maxSizeInBytes) {
        try {
          const compressedImage = await this.imageCompressionService.compressImage(file, maxSizeInBytes);
          if (!compressedImage) {
            const message = this.translate.instant("SnackBarConstants.IMAGE_TOO_LARGE");
            this.commentService.addSnackBar.emit(message);
            return;
          }
          imageToUpload = compressedImage;
        } catch (error) {
          console.log(error);
          const message = this.translate.instant("SnackBarConstants.IMAGE_COMPRESSION_FAILED");
          this.commentService.addSnackBar.emit(message);
          return;
        }
      }

      const reader = new FileReader();
      this.newImage.push(imageToUpload);
      const image = imageToUpload;
      let newindex = this.newImage.length - 1;
      reader.readAsDataURL(imageToUpload);

      reader.onload = async (e) => {
        if (e.target) {
          const url = e.target.result as string;
          if (url) {
            let newimg = { url: url , size: undefined};

            try {
              const uploadURL = await this.uploadService.uploadNewImages(this.passId, image, newindex, this.widgetNumber);
              let size

              await this.uploadService.calculateImgFileSize(url).then(value => {
                this.imagesSizes[uploadURL] = size
                size = value
              })

              this.newImagesURLs.push(uploadURL);
              if (uploadURL) {
                newimg = { url: uploadURL, size };
                const a = this.imagesEditForm.value.images;
                this.imagesEditForm.patchValue({ images: [...a, newimg] });

                this.updateImgsRef()
                this.url[uploadURL] = url;
                this.imgFromImgNew.push({ form: a.length, new: this.newImage.length - 1 });
              }
            } catch (error) {
              console.error(error);
              const message = this.translate.instant("SnackBarConstants.UPLOAD_IMG_FAILED");
              this.commentService.addSnackBar.emit(message);
            }
          }
        }
      };
    }
  }

  triggerFileInput(): void {
    this.fileInput.nativeElement.click();
  }

  async filesDropped(files: FileHandle[]): Promise<void> {
    this.files = files;
    const maxSizeInBytes = 2 * 1024 * 1024; // 2 MB size limit for compression
    const maxAllowedSizeInBytes = 20 * 1024 * 1024; // 20 MB size limit for direct upload

    files.forEach(async (file, index) => {
      const fileToUpload = file.file;

      // Check if the file size exceeds the maximum allowed size for upload
      if (fileToUpload.size > maxAllowedSizeInBytes) {
        const message = this.translate.instant("SnackBarConstants.IMAGE_FILE_TOO_LARGE");
        this.commentService.addSnackBar.emit(message);
        return;
      }

      let imageToUpload = fileToUpload;

      // Compress the image if it exceeds the 2 MB size limit
      if (fileToUpload.size > maxSizeInBytes) {
        try {
          const compressedImage = await this.imageCompressionService.compressImage(fileToUpload, maxSizeInBytes);
          if (!compressedImage) {
              const message = this.translate.instant("SnackBarConstants.IMAGE_TOO_LARGE");
              this.commentService.addSnackBar.emit(message);
              return;
          }
          imageToUpload = compressedImage;
        } catch (error) {
          console.error(error);
          const message = this.translate.instant("SnackBarConstants.IMAGE_COMPRESSION_FAILED");
          this.commentService.addSnackBar.emit(message);
          return;
        }
      }

      this.newImage.push(imageToUpload);
      const image = imageToUpload;
      const newUrl = files[index].url;
      let newindex = this.newImage.length - 1;
      if (fileToUpload.type === 'image/jpeg' || fileToUpload.type === 'image/png' || fileToUpload.type == 'image/webp') {
        if (newUrl) {
          this.drop = true;
          let newimg = { url: newUrl , size: undefined};

          try {
            const uploadURL = await this.uploadService.uploadNewImages(this.passId, image, newindex, this.widgetNumber);
            this.newImagesURLs.push(uploadURL);
            this.supportUrl(uploadURL, newindex)

            let size
              await this.uploadService.calculateImgFileSize(newUrl['changingThisBreaksApplicationSecurity']).then(value => {
                this.imagesSizes[uploadURL] = size
                size = value
              })

            if (uploadURL) {
              newimg = { url: uploadURL, size: size };
              const a = this.imagesEditForm.value.images;
              this.imagesEditForm.patchValue({ images: [...a, newimg] });
              this.url[uploadURL] = newUrl;
              this.imgFromImgNew.push({ form: a.length, new: this.newImage.length - 1 });
            }

          } catch (error) {
            console.error(error);
            const message = this.translate.instant("SnackBarConstants.UPLOAD_IMG_FAILED");
            this.commentService.addSnackBar.emit(message);
          }
        }
      } else {
        const message = this.translate.instant('SnackBarConstants.UNSUPPORTED_IMG_TYPE');
        this.commentService.addSnackBar.emit(message);
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  editImg(image, index){
    let dialogRef= this.dialog.open(MediaDetailsDialogComponent, {
      data: {
        values: image,
        type: this.imagesEditForm.value.type,
        passId: this.passId,
        widgetNumber:this.widgetNumber,
        translationMode: this.data.translateMode,
        translateData: this.data.translateData,
        anchorOptions: this.anchorOptions,
        childPages:this.childPages,
      },
    });

    dialogRef.afterClosed().subscribe(result => {
     const changes = result[1]
      if(changes){
        const values = result[0]
        const fields = Object.keys(values)
        if(fields?.length!=0){
          fields.forEach( key => {
            image[key] = values[key]
            if(key === 'url' && this.data.translateMode)
              this.url[image.id] = supportUrlStructure( image.url, this.passId, this.settingsService.contextId$)
            else
              this.supportUrl(image.url, index)
          })
          delete image.visibility
          const images = this.imagesEditForm.value.images
          images[index] = image
          this.imagesEditForm.patchValue({images})
          this.updateImgsRef()

        }
      }
    });
  }

  deleteImg(x:any,index:any){
    this.allImagesAfterDelete = []
    const allimages = deepCopy(this.imagesEditForm.value.images)
    allimages.splice(index,1)

    this.imagesEditForm.patchValue({images: allimages})

    this.updateImgsRef()

    this.allImages = allimages
    this.allImagesAfterDelete = allimages
    this.imagesToDelete.push(x)
  }

  returnChangesFilters($event){
    this.imagesEditForm.patchValue({filter: $event.newFilters})
  }

  returnChangesStyle($event){
    this.imagesEditForm.patchValue({widgetStyle: $event.newStyle})
  }

  async returnChangesDragFileBrowseUI($event){

    if($event){
      const { returnSupportUrl,returnUrlData, enterUrl, returnUrlType } = $event
      this.dragFileData = $event

      if(enterUrl)
        this.openMediaURL()

      if(returnUrlType && returnUrlType!= 'media')
        this.newImagesURLs.push(returnSupportUrl)

      if(returnUrlData)
        this.url[returnSupportUrl] = returnUrlData

      if(returnSupportUrl){
        let size = undefined
        let newimg = { url: returnSupportUrl, size };
        const a = this.imagesEditForm.value.images;
        this.imagesEditForm.patchValue({ images: [...a, newimg] });
        this.imgFromImgNew.push({ form: a.length, new: this.newImage.length - 1 });
        this.dragFileData.returnSupportUrl = undefined
        this.dragFileData.returnUrlData = undefined
        this.dragFileData.enterUrl = false
      }

    }else
      this.dragFileData.enterUrl = false
  }

  openMediaURL(){
    const index = this.imagesEditForm.value.images.length
    let dialogRef= this.dialog.open(MediaDetailsDialogComponent, {
      data: {
        values: '',
        type: this.imagesEditForm.value.type,
        passId: this.passId,
        widgetNumber:this.widgetNumber,
        translateMode: this.data.translateMode,
        translateData: this.data.translateData
      },
    });

    dialogRef.afterClosed().subscribe(result => {
      if(result[1]){
        if(result[0].url){
          const data = result[0]
          delete data.visibility
          let imagesform = deepCopy(this.imagesEditForm.value.images)
          imagesform.push(data)
          this.supportUrl(result[0].url, index)
          this.imagesEditForm.patchValue({images: imagesform})
          this.dragFileData.enterUrl = false
        }
      }else{
        this.dragFileData.enterUrl = false
      }
    });
  }

  browserFromMediaFolder(){
    let dialogRef= this.dialog.open(MediaDialogHandlerComponent, {
      data: {
        supportedFiles: ['image/jpeg', 'image/png', 'image/webp']
      },
    });

    dialogRef.afterClosed().subscribe(async result => {
      if(result[0]){
        const url = result[1]
        const supportUrl = result[2]

        if (url) {
          let size
          await this.uploadService.calculateImgFileSize(supportUrl).then(value => {
            this.imagesSizes[url] = size
            size = value
          })

          let newimg = { url: url, size: undefined };

          const a = this.imagesEditForm.value.images;
          this.imagesEditForm.patchValue({ images: [...a, newimg] });

          this.url[url] = supportUrl;
          this.imgFromImgNew.push({ form: a.length, new: this.newImage.length - 1 });
        }
      }
    });
  }

  onReorder(ev: CdkDragDrop<any[]>) {
    if(!this.data.translateData){
      const images = this.imagesEditForm.get('images')?.value

      if (ev.previousIndex === ev.currentIndex) return;
      moveItemInArray(images, ev.previousIndex, ev.currentIndex);

      this.imagesEditForm.patchValue({ images });
      this.updateImgsRef()

      this.allImages = this.imagesRef;
      this.allImagesAfterDelete = this.imagesRef;
    }
  }

  selectLayout(layout: string) {
    this.imagesEditForm.patchValue({ layout });
    this.layoutSelected = layout;
    this.layoutImages = this.layoutTypes.find(l => l.value === layout)?.images;
    this.layoutBad = !this.imagesEditForm.value.keyPath?.trim()
      && (this.layoutImages ?? 0) > (this.imagesEditForm.value?.images?.length ?? 0);
  }
}
